import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileCheck, Users, AlertTriangle, Scale, XCircle } from "lucide-react";
import ThemeToggle from "../common/ThemeToggle";

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      {/* Navigation */}
      <nav className="fixed right-0 left-0 top-0 shadow-lg bg-white dark:bg-slate-900/95 backdrop-blur-lg border-b border-gray-100 dark:border-gray-800 transition-all duration-300 z-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex justify-between items-center h-20 gap-8">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="/usiu-logo-02.png"
                alt="USIU Logo"
                className="h-12 w-12 object-contain"
                onError={(e) => {
                  e.target.src = "/usiu-logo.png";
                }}
              />
              <div>
                <h1 className="text-xl font-display font-bold bg-gradient-usiu-primary bg-clip-text text-transparent">
                  USIU-Africa
                </h1>
                <p className="text-xs text-gray-600 dark:text-gray-300 font-medium">Counseling & Wellbeing</p>
              </div>
            </Link>

            <div className="flex items-center gap-4">
              <ThemeToggle />
              <Link
                to="/"
                className="flex items-center gap-2 text-gray-700 dark:text-gray-200 font-medium px-4 py-2 rounded-2xl hover:bg-usiu-blue-50 dark:hover:bg-slate-800 transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="inline-block mb-4">
              <span className="bg-usiu-blue-100 dark:bg-usiu-blue-900/30 text-usiu-blue-700 dark:text-usiu-blue-300 px-4 py-2 rounded-full text-sm font-semibold">
                Legal
              </span>
            </div>
            <h1 className="text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
              Terms of Use
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Last updated: January 19, 2026
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg dark:prose-invert max-w-none">
            {/* Introduction */}
            <section className="mb-12">
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600 dark:border-blue-400 p-6 rounded-r-lg mb-8">
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  Welcome to the USIU-Africa Counseling Center platform. By accessing or using our services, you agree to be bound by these Terms of Use. Please read them carefully before using our counseling services and platform.
                </p>
              </div>
            </section>

            {/* Acceptance of Terms */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-blue-100 dark:bg-blue-900/30 w-12 h-12 rounded-xl flex items-center justify-center">
                  <FileCheck className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white m-0">
                  Acceptance of Terms
                </h2>
              </div>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  By creating an account and using the USIU-Africa Counseling Center platform, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use and our Privacy Policy.
                </p>
                <p>
                  If you do not agree to these terms, you must not access or use our services.
                </p>
              </div>
            </section>

            {/* Eligibility */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-100 dark:bg-green-900/30 w-12 h-12 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white m-0">
                  Eligibility
                </h2>
              </div>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>Our counseling services are available to:</p>
                <ul className="space-y-2">
                  <li>Current USIU-Africa students (undergraduate and graduate)</li>
                  <li>USIU-Africa faculty and staff members</li>
                  <li>Individuals who are 18 years of age or older</li>
                  <li>Minors with parental or guardian consent</li>
                </ul>
                <p>
                  You must provide accurate and complete information when creating your account and keep your login credentials secure.
                </p>
              </div>
            </section>

            {/* Services Provided */}
            <section className="mb-12">
              <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white mb-6">
                Services Provided
              </h2>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>The USIU-Africa Counseling Center provides:</p>
                <ul className="space-y-2">
                  <li>Individual counseling sessions</li>
                  <li>Group therapy and support sessions</li>
                  <li>Crisis intervention services</li>
                  <li>Virtual counseling options</li>
                  <li>Mental health assessments and referrals</li>
                  <li>Wellness programs and workshops</li>
                </ul>
                <p>
                  Our services are designed to support mental health and wellbeing but do not replace emergency medical care or psychiatric treatment when needed.
                </p>
              </div>
            </section>

            {/* User Responsibilities */}
            <section className="mb-12">
              <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white mb-6">
                User Responsibilities
              </h2>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>As a user of our services, you agree to:</p>
                <ul className="space-y-2">
                  <li>Provide accurate and truthful information about your mental health concerns</li>
                  <li>Attend scheduled appointments or cancel with at least 24 hours notice</li>
                  <li>Respect the confidentiality of other clients in group sessions</li>
                  <li>Follow treatment recommendations and care plans</li>
                  <li>Communicate openly with your counselor about your progress and concerns</li>
                  <li>Not use the platform for any unlawful or prohibited purposes</li>
                  <li>Maintain the security of your account credentials</li>
                </ul>
              </div>
            </section>

            {/* Confidentiality */}
            <section className="mb-12">
              <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white mb-6">
                Confidentiality
              </h2>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  All counseling sessions and communications are confidential and protected under professional ethical standards. However, confidentiality may be limited in the following situations:
                </p>
                <ul className="space-y-2">
                  <li>When there is a serious threat of harm to yourself or others</li>
                  <li>When child or elder abuse is suspected</li>
                  <li>When required by law or court order</li>
                  <li>In emergency situations to protect your safety</li>
                </ul>
                <p>
                  For more details, please refer to our <Link to="/privacy-policy" className="text-usiu-blue-600 dark:text-usiu-blue-400 hover:underline">Privacy Policy</Link>.
                </p>
              </div>
            </section>

            {/* Limitations and Disclaimers */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-orange-100 dark:bg-orange-900/30 w-12 h-12 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white m-0">
                  Limitations and Disclaimers
                </h2>
              </div>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  <strong>Not Emergency Services:</strong> Our counseling services are not designed for emergency situations. If you are experiencing a mental health crisis, please contact:
                </p>
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <ul className="space-y-1">
                    <li><strong>Emergency Services:</strong> 999 or 112</li>
                    <li><strong>USIU Security:</strong> +254 730 116 748</li>
                    <li><strong>Kenya Red Cross:</strong> 1199</li>
                  </ul>
                </div>
                <p>
                  <strong>Professional Judgment:</strong> While we strive to provide the highest quality care, counseling outcomes cannot be guaranteed. Treatment effectiveness varies based on individual circumstances.
                </p>
                <p>
                  <strong>Platform Availability:</strong> We aim to maintain platform availability but cannot guarantee uninterrupted access. We are not liable for technical issues beyond our control.
                </p>
              </div>
            </section>

            {/* Liability */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-purple-100 dark:bg-purple-900/30 w-12 h-12 rounded-xl flex items-center justify-center">
                  <Scale className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white m-0">
                  Limitation of Liability
                </h2>
              </div>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  To the fullest extent permitted by law, USIU-Africa Counseling Center and its staff shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services.
                </p>
                <p>
                  Our liability is limited to the provision of professional counseling services in accordance with ethical and professional standards.
                </p>
              </div>
            </section>

            {/* Termination */}
            <section className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-red-100 dark:bg-red-900/30 w-12 h-12 rounded-xl flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white m-0">
                  Termination
                </h2>
              </div>

              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <p>
                  We reserve the right to suspend or terminate your access to our services if you:
                </p>
                <ul className="space-y-2">
                  <li>Violate these Terms of Use</li>
                  <li>Provide false or misleading information</li>
                  <li>Engage in abusive or threatening behavior toward staff</li>
                  <li>Repeatedly miss scheduled appointments without notice</li>
                  <li>Use the platform for unlawful purposes</li>
                </ul>
                <p>
                  You may also terminate your use of our services at any time by contacting us. Your health records will be retained as required by law.
                </p>
              </div>
            </section>

            {/* Changes to Terms */}
            <section className="mb-12">
              <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white mb-6">
                Changes to Terms
              </h2>

              <p className="text-gray-700 dark:text-gray-300">
                We may update these Terms of Use from time to time. We will notify you of any significant changes by posting the updated terms on our platform and updating the "Last updated" date. Your continued use of our services after such changes constitutes your acceptance of the updated terms.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-12">
              <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white mb-6">
                Governing Law
              </h2>

              <p className="text-gray-700 dark:text-gray-300">
                These Terms of Use are governed by and construed in accordance with the laws of the Republic of Kenya. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts of Kenya.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-12">
              <h2 className="text-3xl font-display font-bold text-gray-900 dark:text-white mb-6">
                Contact Information
              </h2>

              <div className="bg-gray-50 dark:bg-slate-800 rounded-2xl p-6 space-y-3">
                <p className="text-gray-700 dark:text-gray-300">
                  If you have questions about these Terms of Use, please contact us:
                </p>
                <div className="space-y-2 text-gray-700 dark:text-gray-300">
                  <p><strong>Email:</strong> counsel@usiu.ac.ke</p>
                  <p><strong>Phone:</strong> +254 730 116 748</p>
                  <p><strong>Address:</strong> USIU-Africa, Thika Road, Kasarani, P.O. Box 14634-00800, Nairobi, Kenya</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-slate-950 text-white py-8 px-6 border-t border-gray-800 mt-auto">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} USIU-Africa Counseling Center. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfUse;
